import {
  BasedataIcon,
  BulkEditIcon,
  Button,
  Checkbox,
  CloseIcon,
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  FilterIcon,
  SearchInput,
} from '@bp/ui-components';
import classNames from 'classnames';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { PersonLabelSource, PersonLabelType } from 'components/Persons/types/personTypes';
import {
  getPersonLabelSourceTranslation,
  sortByAccessRoleTranslation,
  sortByAdminRolesTranslation,
  sortByStatusTranslation,
  sortByValue,
  sortPersonLabelSource,
  uniqueByLabelSourceUuids,
  uniqueByLabelValue,
} from 'components/Persons/utils/personUtils';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PersonLabel } from './PersonLabel';
import styles from './PersonsLabelFilter.module.scss';

const PersonsLabelFilterFragment = graphql(`
  fragment PersonsLabelFilterFields on Query {
    labels: personLabels(schoolYearUuid: $schoolyearUuid, context: $labelContext, uuids: $labelUuids) {
      __typename
      context
      items {
        __typename
        uuid
        labels {
          __typename
          source
          sourceUuids
          value
          subValue
          tooltip
        }
      }
    }
  }
`);

type PersonsLabelFilterProps = {
  selectedLabels: PersonLabelType[];
  showSourcesFilter?: boolean;
  availableSources?: PersonLabelSource[];
  activeSources?: PersonLabelSource[];
  showQuickSelection?: boolean;
  showSearch?: boolean;
  showCount?: boolean;
  currentCount?: number;
  maxCount?: number;
  showAddButton?: boolean;
  addButtonContent?: ReactNode;
  showBulkEdit?: boolean;
  bulkEditContent?: DropdownMenuItem[];
  bulkEditDisabled?: boolean;
  filterMode: Record<PersonLabelSource, 'and' | 'or'>;
  onQuickSelection?: (label: PersonLabelType) => void;
  onLabelClose: (label: PersonLabelType) => void;
  onCloseAll: () => void;
  onSearch?: (value: string) => void;
  onSourceFilter?: (sources: PersonLabelSource[]) => void;
  onFilterModeChange?: (source: PersonLabelSource, mode: 'and' | 'or') => void;
  data: FragmentType<typeof PersonsLabelFilterFragment> | undefined;
};

export const PersonsLabelFilter = ({
  selectedLabels,
  showSourcesFilter,
  availableSources,
  activeSources,
  showQuickSelection,
  showSearch,
  showCount,
  currentCount,
  maxCount,
  showAddButton,
  addButtonContent,
  showBulkEdit,
  bulkEditContent,
  bulkEditDisabled,
  filterMode,
  onQuickSelection,
  onLabelClose,
  onCloseAll,
  onSearch,
  onSourceFilter,
  onFilterModeChange,
  data,
}: PersonsLabelFilterProps) => {
  const { t } = useTranslation();

  const [search, setSearch] = useState<string>('');

  const fragment = getFragmentData(PersonsLabelFilterFragment, data);

  const toggleFilterMode = useCallback(
    (source: PersonLabelSource) => {
      const currentMode = filterMode[source] || 'or';
      const newMode = currentMode === 'or' ? 'and' : 'or';
      onFilterModeChange?.(source, newMode);
    },
    [filterMode, onFilterModeChange],
  );

  const labels: PersonLabelType[] = useMemo(
    () =>
      (fragment?.labels.items ?? []).flatMap((person) =>
        person.labels.map(
          (label) =>
            ({
              personUuid: person.uuid,
              source: label.source,
              sourceUuids: label.sourceUuids,
              value: label.value,
              subValue: label.subValue,
              tooltip: label.tooltip,
            }) as PersonLabelType,
        ),
      ),
    [fragment?.labels.items],
  );

  const sourceFilterChanged = useCallback(
    (source: PersonLabelSource) => {
      const newSources = activeSources?.includes(source)
        ? activeSources.filter((s) => s !== source)
        : [...(activeSources ?? []), source];
      onSourceFilter?.(newSources);
    },
    [activeSources, onSourceFilter],
  );

  const getFilterMenuEntry = useCallback(
    (source: PersonLabelSource): DropdownMenuItem => {
      const hintClasses = classNames(styles['color-hint'], styles[source.toLowerCase()]);

      return {
        type: 'component',
        node: (
          <div className={styles['filter-menu-entry']} onClick={() => onSourceFilter && sourceFilterChanged(source)}>
            <Checkbox
              className={styles.checkbox}
              name={`filter-menu-${source}`}
              checked={activeSources?.includes(source)}
              onChange={() => onSourceFilter && sourceFilterChanged(source)}
            />
            <div className={hintClasses}></div>
            <div className={styles.label}>{getPersonLabelSourceTranslation(source)}</div>
          </div>
        ),
      };
    },
    [activeSources, onSourceFilter, sourceFilterChanged],
  );

  const filterMenuEntries: DropdownMenuItem[] = useMemo(() => {
    return [
      {
        label: t('common.all'),
        onClick: () => onSourceFilter && availableSources && onSourceFilter(availableSources),
      },
      {
        label: t('common.none'),
        onClick: () => onSourceFilter && onSourceFilter([]),
      },
      {
        type: 'ruler',
      },
      ...(availableSources?.sort((a, b) => sortPersonLabelSource(a, b)).map((option) => getFilterMenuEntry(option)) ??
        []),
    ];
  }, [availableSources, getFilterMenuEntry, onSourceFilter, t]);

  const quickSelections: { source: string; labels: PersonLabelType[]; originalSource: PersonLabelSource }[] =
    useMemo(() => {
      const entries: { source: string; labels: PersonLabelType[]; originalSource: PersonLabelSource }[] = [];

      for (const source of activeSources?.sort((a, b) => sortPersonLabelSource(a, b)) ?? []) {
        const sourceLabels = labels.filter((l) => l.source === source);
        const uniqueLabels =
          source !== 'ClassGroup' ? uniqueByLabelValue(sourceLabels) : uniqueByLabelSourceUuids(sourceLabels);
        if (source === 'Class' || source === 'ClassGroup') {
          uniqueLabels.sort(sortByValue);
        }
        if (source === 'UserStatus') {
          uniqueLabels.sort(sortByStatusTranslation);
        } else if (source === 'ProfileRole') {
          uniqueLabels.sort(sortByAccessRoleTranslation);
        } else if (source === 'AccessRole') {
          uniqueLabels.sort(sortByAdminRolesTranslation);
        }

        if (uniqueLabels.length > 0) {
          entries.push({
            source: getPersonLabelSourceTranslation(source),
            originalSource: source,
            labels: uniqueLabels.map((label) => ({
              ...label,
              disabled:
                selectedLabels.map((l) => l.source).includes(source) &&
                !selectedLabels.find((l) => l.value === label.value && l.subValue === label.subValue),
            })),
          });
        }
      }

      return entries;
    }, [activeSources, labels, selectedLabels]);

  return (
    <div className={styles['persons-label-filter']}>
      <div className={styles.actions}>
        {/* 'Label anzeigen' */}
        {showSourcesFilter && (
          <div className={styles['label-filter']}>
            <Dropdown
              trigger={
                <Button hierarchy='secondary' icon={<FilterIcon strokeWidth={2.5} />}>
                  {t('persons.labels.filterLabels')}
                </Button>
              }
              usePortal={false}
            >
              <DropdownMenu data={filterMenuEntries} />
            </Dropdown>
            <div className={styles['active-count']}>{activeSources?.length}</div>
          </div>
        )}

        {/* 'Schnellauswahl' */}
        {showQuickSelection && (
          <div className={styles['quick-selection']}>
            <Dropdown
              maxHeight='m'
              trigger={
                <Button hierarchy='secondary' icon={<BasedataIcon strokeWidth={2.5} />}>
                  {t('common.quickSelection')}
                </Button>
              }
              usePortal={false}
            >
              <div className={styles['quick-selection-menu']}>
                {quickSelections.map((row) => {
                  return (
                    <div key={row.source} className={styles['menu-entry']}>
                      <div className={styles.headline}>{row.source}</div>
                      {row.originalSource === 'ClassGroup' ? (
                        <>
                          {Object.entries(
                            row.labels.reduce(
                              (groups, label) => {
                                const value = label.subValue ?? '';
                                if (!groups[value]) {
                                  groups[value] = [];
                                }
                                groups[value].push(label);
                                return groups;
                              },
                              {} as Record<string, PersonLabelType[]>,
                            ),
                          ).map((item) => {
                            return (
                              <div key={item[0]} className={styles['class-group']}>
                                <div className={styles['class-group-name']}>
                                  {`${t('classes.title', { count: 1 })} ${item[0]}`}
                                </div>
                                <div className={styles.labels}>
                                  {item[1].map((label) => (
                                    <PersonLabel
                                      key={`quick-select-${label.value}-${label.subValue}`}
                                      {...label}
                                      subValue={label.source === 'ClassGroup' ? label.subValue : null}
                                      onClick={() => onQuickSelection && onQuickSelection(label)}
                                    />
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </>
                      ) : (
                        <div className={styles.labels}>
                          {row.labels.map((label) => (
                            <PersonLabel
                              key={`quick-select-${label.value}`}
                              {...label}
                              subValue={label.source === 'ClassGroup' ? label.subValue : null}
                              onClick={() => onQuickSelection && onQuickSelection(label)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Dropdown>
          </div>
        )}

        {/* 'Personen durchsuchen' */}
        {showSearch && (
          <SearchInput
            name='search'
            value={search}
            placeholder={t('persons.search')}
            onChange={(e) => {
              setSearch(e.target.value);
              onSearch && onSearch(e.target.value);
            }}
          />
        )}

        {/* X von Y Personen angezeigt */}
        {showCount && currentCount !== undefined && maxCount !== undefined && (
          <div className={styles.count}>
            {t('persons.labels.filterCount', { current: currentCount, max: maxCount })}
          </div>
        )}

        <div className={styles['right-section']}>
          {/* 'Aktion für Auswahl' */}
          {showBulkEdit && bulkEditContent && bulkEditContent.length > 0 && (
            <Dropdown
              triggerClass={styles['bulk-edit']}
              trigger={
                <Button hierarchy='tertiary' icon={<BulkEditIcon />} disabled={bulkEditDisabled}>
                  {t('common.bulkEdit')}
                </Button>
              }
              disabled={bulkEditDisabled}
              usePortal={false}
            >
              <DropdownMenu data={bulkEditContent} />
            </Dropdown>
          )}

          {/* 'Person hinzufügen' */}
          {showAddButton && addButtonContent}
        </div>
      </div>

      {selectedLabels.length > 0 && (
        <div className={styles['selected-labels']}>
          {Object.entries(
            selectedLabels
              .sort((a, b) => sortPersonLabelSource(a.source, b.source))
              .reduce(
                (groups, label) => {
                  const source = label.source;
                  if (!groups[source]) {
                    groups[source] = [];
                  }
                  groups[source].push(label);
                  return groups;
                },
                {} as Record<PersonLabelSource, PersonLabelType[]>,
              ),
          ).map(([sourceKey, labels]) => {
            const source = sourceKey as PersonLabelSource;
            return (
              <div key={source} className={styles['label-group']}>
                {labels.map((label) => (
                  <PersonLabel
                    key={`selected-label-${label.uuid ? label.uuid : `${label.source}-${label.value}-${label.subValue}`}`}
                    {...label}
                    subValue={label.source === 'ClassGroup' ? label.subValue : null}
                    disabled={false}
                    onClick={() => onLabelClose(label)}
                  />
                ))}
                {labels.length > 1 && (
                  <div className={styles['filter-mode-toggle']} onClick={() => toggleFilterMode(source)}>
                    {filterMode[source] === 'and' ? t('common.and').toUpperCase() : t('common.or').toUpperCase()}
                  </div>
                )}
              </div>
            );
          })}
          <div className={styles['close-all']} onClick={() => onCloseAll()}>
            <CloseIcon className={styles.icon} />
            {t('persons.labels.removeAll')}
          </div>
        </div>
      )}
    </div>
  );
};
